// ═══════════════════════════════════════════════════════════════
// ATTACHMENT SERVICE
// Path: src/services/attachmentService.js
//
// Upload, list, and delete member file attachments via
// Supabase Storage (member-attachments bucket).
// Metadata stored in members.attachments JSONB column.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

const BUCKET = "member-attachments";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * Upload a file for a member and return the attachment metadata object.
 */
export const attachmentService = {
  async upload(memberId, file) {
    if (!memberId || !file) throw new Error("Member ID and file are required");
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`);
    }

    // Sanitize filename: keep extension, replace spaces, add timestamp for uniqueness
    const ext = file.name.includes(".") ? file.name.split(".").pop() : "";
    const baseName = file.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .slice(0, 80);
    const timestamp = Date.now();
    const storagePath = `${memberId}/${timestamp}_${baseName}${ext ? "." + ext : ""}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, file, { contentType: file.type });

    if (uploadError) {
      debugLog.error("attachmentService.upload", uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);

    const attachment = {
      name: file.name,
      path: storagePath,
      url: data.publicUrl,
      size: file.size,
      type: file.type || "application/octet-stream",
      uploadedAt: new Date().toISOString(),
    };

    debugLog.action("attachmentService.upload", { memberId, path: storagePath });
    return attachment;
  },

  async remove(storagePath) {
    if (!storagePath) return;
    const { error } = await supabase.storage.from(BUCKET).remove([storagePath]);
    if (error) {
      debugLog.error("attachmentService.remove", error);
      throw error;
    }
    debugLog.action("attachmentService.remove", { path: storagePath });
  },

  /**
   * Save the full attachments array to the member record.
   */
  async saveAttachments(memberId, attachments) {
    const { error } = await supabase
      .from("members")
      .update({ attachments })
      .eq("id", memberId);
    if (error) {
      debugLog.error("attachmentService.saveAttachments", error);
      throw error;
    }
  },

  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return "0 B";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  },
};
